// Vue
import Vue from "vue";
import moment from "moment-timezone";
import VueMoment from "vue-moment";
moment.tz.setDefault("UTC");

// PWA Support
import "./registerServiceWorker";

// Vuex
import { sync } from "vuex-router-sync";
import store from "./store";

// Router
import router from "./router";

// Vuex router sync
sync(store, router);

// Plugins
import { FilePreviewPlugin } from "@sgs/file-preview-client-vue";

// Components
import App from "./App.vue";

// Env
import { env } from "./env";

// Auth
import { MsalBroadcast, MsalPlugin as Auth, LogLevel } from "./core/auth";

import httpClient from "./core/http/http-client";

// Application Insights
import { ApplicationInsights } from "@microsoft/applicationinsights-web";

// Vuetify
import vuetify from '@/plugins/vuetify';

// i18n
import i18n from '@/i18n';

// Setup
Vue.config.productionTip = false;

Vue.use(VueMoment, { moment });

// Global Functions
Vue.prototype.isMobile = () => {
  return (vuetify.framework.presets as any).framework.breakpoint.smAndDown;
};
// Global Functions
Vue.prototype.isSmallMobile = () => {
  return (vuetify.framework.presets as any).framework.breakpoint.xsOnly;
};
Vue.prototype.isOldEdge = () => {
  return /Edge/.test(navigator.userAgent);
};
Vue.prototype.isChrome = () => {
  return /Chrome/.test(navigator.userAgent);
};

Vue.prototype.selectedLanguage = (value: string) => {
  if (!value) {
    return env.i18nFallbackLocale;
  }
  return value;
};

Vue.filter("capitalize", (value: string) => {
  if (!value) {
    return "";
  }
  value = value.toString();
  return value.charAt(0).toUpperCase() + value.slice(1);
});

// Auth init
const msalEvents = [
  "msal:loginSuccess",
  "msal:loginFailure",
  "msal:acquireTokenFailure",
  "msal:notAuthorized",
];

msalEvents.forEach((event) => {
  MsalBroadcast.subscribe(event, (e: any) => {
    console.info(`[MSAL] [${event.replace("msal:", "")}]`, e);
  });
});

export function logger(logLevel: any, message: any, piiEnabled: any) {
  console.log(`[MSAL] ${logLevel}`, message);
}

import { MsalConfig } from "./core/auth/msal-config";
function setUpMsalConfiguration(env: any): MsalConfig {
  const envAuth = env.auth;
  const defaultConfig = new MsalConfig();
  const userConfig: MsalConfig = {
    clientId: envAuth.clientId,
    authority: envAuth.authority,
    redirectUri: envAuth.redirectUri,
    postLogoutRedirectUri: envAuth.postLogoutRedirectUri,
    navigateToLoginRequestUrl: envAuth.navigateToLoginRequestUrl,
    cacheLocation: envAuth.cacheLocation,
    logger,
    isAngular: true,
    consentScopes: envAuth.consentScopes.split(","),
    unprotectedResources: envAuth.unprotectedResources,
    protectedResourceMap: envAuth.protectedResourceMap,
    loadFrameTimeout: envAuth.loadFrameTimeout,
  };
  userConfig.protectedResourceMap = [
    [env.apiUrl, envAuth.apiScopes.split(",")],
  ];
  const resultSettings = mergeDeep(defaultConfig, userConfig, false);
  return resultSettings;
}

function mergeDeep(
  sourcePartialObject: any,
  targetFullObject: any,
  override = true
): typeof targetFullObject {
  if (isObject(targetFullObject) && isObject(sourcePartialObject)) {
    Object.keys(sourcePartialObject).forEach((key) => {
      if (isObject(sourcePartialObject[key])) {
        if (!(key in targetFullObject))
          Object.assign(targetFullObject, { [key]: sourcePartialObject[key] });
        else {
          targetFullObject[key] = mergeDeep(
            sourcePartialObject[key],
            targetFullObject[key],
            override
          );
        }
      } else {
        if (!targetFullObject[key] || override) {
          Object.assign(targetFullObject, { [key]: sourcePartialObject[key] });
        }
      }
    });
  }
  return targetFullObject;
}

function isObject(item: any) {
  return item && typeof item === "object" && !Array.isArray(item);
}

const msalConfig = setUpMsalConfiguration(env);
Vue.use(Auth, {
  config: msalConfig,
  router,
});

httpClient.setAuthContext(Vue.prototype.$authContext);

// SGS File Preview Plugin
Vue.use(FilePreviewPlugin);

// Vue init
const app = new Vue({
  el: "app",
  render: (h: any) => h(App),
  router,
  store,
  vuetify,
  i18n,
  components: {
    App,
  },
});

// App insights init
const appInsights = new ApplicationInsights({
  config: {
    instrumentationKey: env.appInsights,
    enableCorsCorrelation: true,
    /* ...Other Configuration Options... */
  },
});
appInsights.loadAppInsights();
